import { Chevron } from "@components/icons"
import TextInput from "@components/inputs/TextInput"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import {
  CUSTOMER_COUNTRY_CODE_KEY,
  MAESTRA_DEVICE_UUID_KEY,
} from "@constants/storageKeys"
import {
  body14Styles,
  body16MainStyles,
  body18Styles,
  h4Styles,
  smallcapsBold13Styles,
} from "@constants/typography"
import { postBulkOrder } from "@utils/api"
import { DesktopOnly, MobileAndTabletOnly } from "@utils/MediaQuery"
import { Form, Formik, FormikHelpers as FormikActions } from "formik"
import React from "react"
import styled from "styled-components"
import * as Yup from "yup"

import Modal, { ModalBaseProps } from "../index"

const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const Row = styled.div`
  @media ${breakpoints.laptop} {
    flex-shrink: 0;
    justify-content: space-between;
    display: flex;
    width: 100%;
  }
`

const Header = styled.div`
  display: flex;
  height: 72px;
  margin-bottom: 40px;
  padding: 0 20px;
  flex-direction: row;
  align-items: center;
  border-bottom: 1px solid ${colors.dividerLineGray};

  @media ${breakpoints.laptop} {
    width: calc(3.5 / 12 * 100vw);
    height: 56px;
    margin-bottom: 100px;
    padding: 0 calc(1 / 12 * 100vw) 0 0;
    border-bottom: none;
  }
`

const HeaderButton = styled.button`
  width: 32px;
  height: 32px;
  margin-right: 8px;
  padding: 0;
  transform: rotate(180deg) translateY(1px);
  background: none;
  border: none;
  outline: none;
  cursor: pointer;

  @media ${breakpoints.laptop} {
    display: none;
  }
`

const HeaderText = styled.div`
  ${smallcapsBold13Styles}

  @media ${breakpoints.laptop} {
    ${h4Styles}
  }
`

const Content = styled.div`
  display: flex;
  padding: 0 20px;
  flex-direction: column;

  @media ${breakpoints.laptop} {
    padding: 0;
    flex-direction: row;
  }
`

const FormHeader = styled.div`
  margin-bottom: 25px;

  @media ${breakpoints.laptop} {
    width: calc(3.5 / 12 * 100vw);
    margin-bottom: 0;
  }
`

const Banner = styled.div`
  ${h4Styles}
  color: ${colors.black};
  text-transform: uppercase;

  @media ${breakpoints.laptop} {
    ${body18Styles}
    width: calc(2 / 12 * 100vw);
    color: ${colors.black};
    text-transform: none;
  }
`

const BannerSubtext = styled.div`
  ${body16MainStyles}
  color: ${colors.mediumGray};

  @media ${breakpoints.laptop} {
    ${body14Styles}
    color: ${colors.mediumGray};
    margin-top: 22px;
  }
`

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;

  @media ${breakpoints.laptop} {
    flex: 1;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-end;
  }
`

const FormControl = styled.div`
  display: flex;
  margin-top: 24px;
  flex-direction: column;

  @media ${breakpoints.laptop} {
    width: calc(50% - 12px);
    margin-top: 0;
    margin-bottom: 34px;
  }
`

const Button = styled.button`
  ${smallcapsBold13Styles}
  width: 100%;
  height: 48px;
  margin-top: 48px;
  padding-top: 4px;
  color: ${colors.offWhite};
  background: ${colors.black};
  border-radius: 2px;
  border: none;
  cursor: pointer;

  &:focus {
    outline-color: ${colors.hoverBlack};
  }

  @media ${breakpoints.laptop} {
    width: 148px;
    margin-top: 58px;
  }
`

interface FormValues {
  email: string
  firstName: string
  lastName: string
  mobilePhone: string
}

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  mobilePhone: Yup.string().required("Phone number is required"),
})

interface BulkOrderProps extends ModalBaseProps {
  showSuccess?: () => void
}

const BulkOrderModal = (props: BulkOrderProps) => {
  const [countryCode, setCountryCode] = React.useState<null | string>(null)
  const [deviceUUID, setDeviceUUID] = React.useState<null | string>(null)
  const _WINDOW = typeof window !== "undefined" ? window : null
  const _LOCALSTORAGE = _WINDOW ? _WINDOW.localStorage : null

  React.useEffect(() => {
    if (_LOCALSTORAGE?.getItem(CUSTOMER_COUNTRY_CODE_KEY)) {
      const rawCountryCode = _LOCALSTORAGE.getItem(CUSTOMER_COUNTRY_CODE_KEY)
      if (rawCountryCode) {
        setCountryCode(JSON.parse(rawCountryCode))
      }
    }

    if (_LOCALSTORAGE?.getItem(MAESTRA_DEVICE_UUID_KEY)) {
      setDeviceUUID(_LOCALSTORAGE.getItem(MAESTRA_DEVICE_UUID_KEY))
    }
  }, [_LOCALSTORAGE])

  const handleSubmit = async (
    values: FormValues,
    formikActions: FormikActions<FormValues>
  ) => {
    const data = {
      countryCode: countryCode,
      deviceUUID: deviceUUID,
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      mobilePhone: values.mobilePhone,
    }

    await postBulkOrder(data, () => {
      if (props.showSuccess) props.showSuccess()
    })

    return formikActions.setSubmitting(false)
  }

  return (
    <Modal {...props}>
      <Container>
        <MobileAndTabletOnly>
          <Header onClick={props.onRequestClose}>
            <HeaderButton>
              <Chevron strokeColor={colors.black} />
            </HeaderButton>
            <HeaderText>Bulk Ordering</HeaderText>
          </Header>
        </MobileAndTabletOnly>
        <DesktopOnly>
          <Header>
            <HeaderText>Bulk Ordering</HeaderText>
          </Header>
        </DesktopOnly>
        <Content>
          <FormHeader>
            <Banner>
              Get a custom quote for a bulk order
              <BannerSubtext>20 product minumum</BannerSubtext>
            </Banner>
          </FormHeader>
          <Formik
            initialValues={{
              email: "",
              firstName: "",
              lastName: "",
              mobilePhone: "",
            }}
            onSubmit={handleSubmit}
            render={(props) => (
              <StyledForm>
                <Row>
                  <FormControl>
                    <TextInput
                      error={props.errors.firstName !== undefined}
                      label="First Name"
                      name="firstName"
                      onBlur={props.handleBlur}
                      onChange={props.handleChange}
                      placeholder="First Name"
                      type="text"
                      value={props.values.firstName}
                    />
                  </FormControl>
                  <FormControl>
                    <TextInput
                      error={props.errors.lastName !== undefined}
                      label="Last Name"
                      name="lastName"
                      onBlur={props.handleBlur}
                      onChange={props.handleChange}
                      placeholder="Last Name"
                      type="text"
                      value={props.values.lastName}
                    />
                  </FormControl>
                </Row>
                <Row>
                  <FormControl>
                    <TextInput
                      error={props.errors.mobilePhone !== undefined}
                      label="Phone Number"
                      name="mobilePhone"
                      onBlur={props.handleBlur}
                      onChange={props.handleChange}
                      placeholder="Phone Number"
                      type="text"
                      value={props.values.mobilePhone}
                    />
                  </FormControl>
                  <FormControl>
                    <TextInput
                      error={props.errors.email !== undefined}
                      label="Email"
                      name="email"
                      onBlur={props.handleBlur}
                      onChange={props.handleChange}
                      placeholder="Email"
                      type="email"
                      value={props.values.email}
                    />
                  </FormControl>
                </Row>
                <Button type="submit">Sign Up</Button>
              </StyledForm>
            )}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={validationSchema}
          />
        </Content>
      </Container>
    </Modal>
  )
}

export default BulkOrderModal
